import {useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {MavenLogo} from '@jobseeker/lotus/assets/images';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getConfigByApp,
  getLocationIds,
  isUserAgentForBot,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {COMPANY, JOBFEED} from '@constants';
import {Chat} from '@components';
import type {GetServerSidePropsContext} from 'next/types';
import loadSEO from '@helpers/loadSEO';
import {useMediaQuery, useTheme} from '@mui/material';
import dynamic from 'next/dynamic';
import {useEffect, useState} from 'react';
import userService from '@jobseeker/common-core/services/user.service';
import {AdsElementType, AppState} from '@jobseeker/common-core/types';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {TrackEventUBA, getUbaPageName} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {jobfeedService} from '@jobseeker/common-core/services';
import qs from 'qs';
import {categoryOptions} from '@jobseeker/feature-course/constants';
import {getListSEOContent, getPageUrl} from '@utils/seo';

const MavenWidget = dynamic(
  () => import('@jobseeker/feature-profile/components/MavenWidget'),
  {
    ssr: false,
  }
);
type JobFeedProps = {
  filters: {
    page: number;
    loc: string[];
  };
  seoConfig: object;
  companyJobs: object;
};
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const JobFeedPage = ({filters, seoConfig, companyJobs}: JobFeedProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBFEED);

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch, false);
  const theme = useTheme();
  const isLarge = useIsLargeDevice();
  const isTabDevice = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [isMavenOpen, setShowMaven] = useState(false);
  const [isMavenSubmitSuccess, setMavenSubmit] = useState(false);
  const {
    profile: {showMaven},
  } = useSelector((state: AppState) => state.user);

  useEffect(() => {
    const haveShownMaven = Boolean(localStorage.getItem('shownMaven'));
    // const mavenVisibility = true && !haveShownMaven;
    const mavenVisibility = !!(showMaven && !haveShownMaven);
    setShowMaven(mavenVisibility);
  }, [showMaven]);

  // useEffect(() => {
  //   if (isMavenOpen) {
  //     // localStorage.setItem('shownMaven', 'true');
  //   }
  // }, [isMavenOpen]);

  const onMavenSubmit = async (formData: any) => {
    const respData: any = await userService.updateDiversity(formData);
    if (respData.message) {
      // enqueueSnackbar(respData.message, {variant: 'success'});
      localStorage.setItem('shownMaven', 'true');
      // setShowMaven(false);
      setMavenSubmit(true);
    }
  };

  const router = useRouter();
  // const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);
  useEffect(() => {
    TrackEventUBA('companyListPageView', 'view', {
      ref: router.query.ref || '',
      filterExp: router.query.minexp
        ? `${router.query.minexp}-${router.query.maxexp}`
        : '',
      filterCategory: router.query.category || '',
      filterLocation: filters?.loc?.join(',') || '',
      pageName: getUbaPageName(),
      sessionUsername: `${sessionStorage.getItem('userId') || ''}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={[]}
          >
            <JobFeed
              jobFeedType={'Company'}
              catOrTagId={''}
              catOrTagTitle={'Companies'}
              showProfileCard={isTabDevice ? false : true}
              showFeatured={true}
              showLearning={true}
              showLearningCenterCTA={true}
              preFilters={filters}
              companyJobs={companyJobs}
            />
            {isLarge && isAppIimjobs && (
              <MavenWidget
                show={isMavenOpen}
                imgUrl={MavenLogo}
                onClose={() => setShowMaven(false)}
                onSubmit={onMavenSubmit}
                isSuccess={isMavenSubmitSuccess}
              />
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      {!isMavenOpen && <Chat />}
    </>
  );
};

export const getServerSideProps = wrapper.getStaticProps(
  store => async (context: GetServerSidePropsContext) => {
    context.res?.setHeader(
      'Cache-Control',
      'public, s-maxage=10, stale-while-revalidate=59'
    );
    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: COMPANY},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }
    if (!isAppIimjobs) {
      const blogs = await jobfeedService.getLatestBlogs('');
      const bloData = (Object.values(blogs) as AdsElementType[])
        ?.map(blog => {
          return {
            link: blog?.link,
            title: blog?.title,
            imageUrl: blog?.featuredImage ?? '',
          };
        })
        ?.slice(0, 3);

      store.dispatch({
        type: 'Ads/set_blog_data',
        payload: bloData,
      });
    }

    store.dispatch({
      type: 'job/jobFeed/set_jobfeed_loading',
      payload: {isLoading: true, loadingSource: 'first_load'},
    });
    const pageNo = parseInt(context.query?.page) || 0;
    const minExp = parseInt(context.query?.minexp) || 0;
    const maxExp = parseInt(context.query?.maxexp) || 0;
    const query = parseInt(context.query?.maxexp) || 0;
    const sort = context.query?.sort || null;
    const category = parseInt(context.query?.category) || -1;
    const location = getLocationIds(context.query?.loc || '');
    const keyword = context.query?.keyword || '';

    if (category >= 0) {
      let catName = '';

      if (category >= 0) {
        catName = categoryOptions.find(({id}) => id === category)?.name || '';
      }

      const updatedQuery = {
        ...context.query,
        ...(catName?.length > 0 ? {category: catName} : {}),
        ...(location?.length > 0 ? {loc: location} : {}),
      };
      const queryString = qs.stringify(updatedQuery);
      return {
        redirect: {
          destination: `${context.resolvedUrl.split('?')[0]}?${queryString}`,
          permanent: false,
        },
      };
    }

    const filters = {
      sort: sort ? [sort] : null,
      page: pageNo > 0 ? pageNo - 1 : 0,
      query: query,
      minexp: minExp,
      maxexp: maxExp,
      loc: [],
    };

    /* eslint-disable @typescript-eslint/no-explicit-any */
    let companyJobs: {docs: any[]; hasMore: boolean} = {
      docs: [],
      hasMore: false,
    };
    const isBotUser = isUserAgentForBot(context);

    if (keyword?.length == 0 && isBotUser) {
      const companies = await jobfeedService.getCompanyList(
        pageNo > 0 ? pageNo - 1 : 0,
        {
          loc: '',
          catId: '',
        }
      );
      companyJobs = {
        docs: [...companies.docs],
        hasMore: companies.hasMore,
      };
    }
    const slugUrl = context?.resolvedUrl || '';

    const seoConfig = await loadSEO(
      COMPANY,
      getListSEOContent(''),
      getPageUrl(slugUrl, pageNo),
      true,
      pageNo
    );
    seoConfig.title = 'Companies- Hirist.tech';
    seoConfig.description = 'Companies- Hirist.tech';

    return {
      props: {
        filters,
        seoConfig,
        companyJobs: companyJobs,
      },
    };
  }
);

export default JobFeedPage;
